import {
    Form as AntForm,
    FormInstance,
    FormProps,
    Modal as AntModal,
    ModalProps as AntModalProps,
} from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button/Button';
import { Form } from 'src/components/Form/Form';
import { Spinner } from 'src/components/Spinner/Spinner';
import { CssColors } from 'src/styles/CssVariables';
import styled from 'styled-components';

export type ModalProps = AntModalProps & {
    formProps?: Omit<FormProps, 'onFinish' | 'initialValues'>;
    form?: FormInstance;
    onFinish?: (values: any) => Promise<any> | FormProps['onFinish'];
    isLoading?: boolean;
    showOnlyLoading?: boolean;
    initialValues?: FormProps['initialValues'];
    headerExtra?: React.ReactNode;
};

export const Modal: React.FC<ModalProps> = ({
    formProps,
    form,
    onFinish,
    width = ModalWidth.Medium,
    initialValues,
    isLoading,
    showOnlyLoading,
    children,
    headerExtra,
    onCancel,
    ...props
}: ModalProps) => {
    const { t } = useTranslation();
    const [innerForm] = AntForm.useForm();
    const targetForm = useMemo(() => {
        return form || innerForm;
    }, [form, innerForm]);

    const okText = props.okText || t('App.Component.Modal.Btn.Ok');
    const cancelText = props.cancelText || t('App.Component.Modal.Btn.Cancel');

    const handleOnCancel = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (isLoading) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        onCancel?.(e as any);
    };

    return (
        <StyledAntModal
            onCancel={handleOnCancel}
            footer={[
                <Button
                    {...props.cancelButtonProps}
                    key="cancel"
                    disabled={isLoading}
                    onClick={handleOnCancel}
                    type="default"
                >
                    {cancelText}
                </Button>,
                <Button
                    {...props.okButtonProps}
                    key="ok"
                    type="primary"
                    disabled={isLoading || props.okButtonProps?.disabled}
                    onClick={(e) => {
                        targetForm.submit();
                        props.onOk?.(e as any);
                    }}
                    loading={isLoading}
                >
                    {okText}
                </Button>,
            ]}
            width={width}
            {...props}
        >
            {showOnlyLoading && isLoading ? (
                <StyledWrapper>
                    <Spinner />
                </StyledWrapper>
            ) : (
                <StyledForm
                    form={targetForm}
                    onFinish={async (values) => {
                        try {
                            await onFinish?.(values);
                            await onCancel?.(true as any);
                        } catch (error) {
                            console.warn(error);
                        }
                    }}
                    initialValues={initialValues}
                    {...formProps}
                >
                    {isLoading && <StyledSpinner />}
                    <StyledHeaderExtra>{headerExtra}</StyledHeaderExtra>
                    <StyledContentBody>{children}</StyledContentBody>
                </StyledForm>
            )}
        </StyledAntModal>
    );
};

const StyledForm = styled(Form)`
    position: relative;
`;

const StyledWrapper = styled.div`
    min-height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const StyledSpinner = styled(Spinner)`
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 99;
    background-color: white;
    opacity: 0.7;
`;

const StyledAntModal = styled(AntModal)`
    max-width: 90%;

    .ant-modal-header {
        padding: 24px 64px 0 24px;
        margin-bottom: 0;
        border-radius: 16px 16px 0 0;
        border-bottom: none;
    }

    .ant-modal-body {
        padding: 0 24px 16px;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 16px;
        overflow: hidden;
    }

    .ant-modal-footer {
        padding: 0 24px 24px;
        text-align: end;
        border-top: none;
    }

    .ant-modal-close {
        top: 16px;
        right: 16px;

        .ant-modal-close-x {
            height: 32px;
            width: 32px;
            line-height: 32px;
        }
    }
`;

const StyledHeaderExtra = styled.div`
    margin-top: 6px;
    font-size: 14px;
    color: ${CssColors.gray};
`;

const StyledContentBody = styled.div`
    padding-top: 16px;
`;

export enum ModalWidth {
    Small = 280,
    Medium = 448,
    Large = 1200,
    ExtraLarge = 1600,
}
